import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Node, Edge, OnNodesChange, OnEdgesChange } from '@xyflow/react';

interface Props {
  title: string;
  tagline: string;
  icon: ReactNode;
  context: string;
  built: string;
  stack: string[];
  highlights: string[];
  nodes?: Node[];
  edges?: Edge[];
  onNodesChange?: OnNodesChange;
  onEdgesChange?: OnEdgesChange;
  screenshots?: { src: string; caption?: string }[];
  liveUrl?: string;
}

const ProjectCaseStudy: React.FC<Props> = ({ title, tagline, icon, context, built, stack, highlights, nodes, edges, onNodesChange, onEdgesChange, screenshots, liveUrl }) => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-100 relative">
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />

      <header className="relative z-20 px-4 sm:px-6 lg:px-8 pt-5">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center rounded-xl border border-zinc-800 bg-[#0d0d0d]/90 backdrop-blur px-5 py-3">
            <Link to="/" className="flex items-center">
              <img src="/pattrnworks_logo_transparent_small.png" alt="PattrnWorks" className="h-9 brightness-0 invert" />
            </Link>
            <nav className="flex items-center space-x-6">
              <Link to="/projects" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors duration-200">All projects</Link>
              <Link to="/#contact" className="hidden sm:flex bg-zinc-100 hover:bg-violet-400 text-zinc-900 text-sm font-medium px-4 py-2 rounded-md transition-colors duration-200 items-center">Get in touch</Link>
            </nav>
          </div>
        </div>
      </header>

      <div className="relative z-10 px-4 md:px-8 py-10 md:py-14">
        <div className="max-w-6xl mx-auto">
          <span className="font-mono text-xs tracking-[0.2em] text-violet-400 uppercase">{'// Case study'}</span>
          <div className="mt-4 flex items-start gap-5">
            <div className="w-14 h-14 rounded-lg border border-zinc-700 bg-zinc-900 flex items-center justify-center flex-shrink-0 text-violet-400">{icon}</div>
            <div>
              <h1 className="text-3xl md:text-5xl font-bold text-zinc-100">{title}</h1>
              <p className="mt-3 text-lg text-zinc-400 max-w-2xl">{tagline}</p>
            </div>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-5">
            <div className="md:col-span-2 space-y-5">
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-7">
                <h2 className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase mb-3">The brief</h2>
                <p className="text-zinc-300 text-sm leading-relaxed">{context}</p>
              </div>
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-7">
                <h2 className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase mb-3">What we built</h2>
                <p className="text-zinc-300 text-sm leading-relaxed">{built}</p>
              </div>
            </div>
            <div className="space-y-5">
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-7">
                <h2 className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase mb-4">Stack</h2>
                <div className="flex flex-wrap gap-2">
                  {stack.map((t) => (
                    <span key={t} className="font-mono text-xs text-zinc-300 border border-zinc-700 bg-zinc-900 px-2.5 py-1 rounded-md">{t}</span>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-7">
                <h2 className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase mb-4">Highlights</h2>
                <ul className="space-y-2">
                  {highlights.map((h) => (
                    <li key={h} className="text-sm text-zinc-300 flex items-start gap-2"><ArrowRight className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />{h}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {screenshots && screenshots.length > 0 && (
            <div className="mt-5 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
              <h2 className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase mb-4 px-2">Product</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {screenshots.map((s) => (
                  <figure key={s.src} className="rounded-lg border border-zinc-800 overflow-hidden bg-zinc-950">
                    <img src={s.src} alt={s.caption || title} className="w-full object-cover object-top" />
                    {s.caption && <figcaption className="px-4 py-2 font-mono text-xs text-zinc-500 border-t border-zinc-800">{s.caption}</figcaption>}
                  </figure>
                ))}
              </div>
            </div>
          )}

          {nodes && nodes.length > 0 && (
            <div className="mt-5 rounded-xl border border-zinc-800 bg-zinc-900/40 p-7">
              <h2 className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase mb-6 px-1">How it works</h2>
              <div className="flex flex-wrap items-stretch gap-3">
                {nodes.map((n, i) => (
                  <React.Fragment key={n.id}>
                    <div className="flex-1 min-w-[150px] rounded-lg border border-zinc-800 bg-zinc-950 p-4">
                      <span className="font-mono text-xs text-violet-400">{String(i + 1).padStart(2, '0')}</span>
                      <p className="mt-2 text-sm text-zinc-200 leading-snug">{String(n.data.label)}</p>
                    </div>
                    {i < nodes.length - 1 && (
                      <ArrowRight className="w-4 h-4 text-zinc-600 self-center flex-shrink-0 hidden lg:block" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {liveUrl && (
            <div className="mt-8 flex">
              <a href={liveUrl} target="_blank" rel="noopener noreferrer" className="bg-zinc-100 hover:bg-violet-400 text-zinc-900 text-sm font-medium px-5 py-2.5 rounded-md transition-colors duration-200 flex items-center">
                Visit live product <ArrowRight className="ml-1.5 h-4 w-4" />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCaseStudy;
